import { DialogClose } from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Textarea } from '@/components/ui-kit/textarea';
import { MarkdownEditor } from '@/modules/gpt-chats/components/markdown-editor';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export const getQnaKbFormSchema = (t: TFunction) =>
  z.object({
    question: z
      .string()
      .trim()
      .max(500, t('QUESTION_MAX_LENGTH'))
      .refine((val) => val.replace(/\s+/g, ' ').trim().length >= 5, {
        message: t('QUESTION_MIN_LENGTH'),
      }),
    answer: z
      .string()
      .trim()
      .max(10000, t('ANSWER_MAX_LENGTH'))
      .refine((val) => val.replace(/\s+/g, ' ').trim().length >= 100, {
        message: t('ANSWER_MIN_LENGTH'),
      }),
  });

export type QnaKbFormValues = z.infer<ReturnType<typeof getQnaKbFormSchema>>;

export const QnaKbFormDefaultValues: QnaKbFormValues = {
  question: '',
  answer: '',
};

type QnAKbFormProps = {
  form: UseFormReturn<QnaKbFormValues>;
  onSubmit: (value: QnaKbFormValues) => void;
  isSaving?: boolean;
  mode: 'add' | 'view';
};

export const QnAKbForm = ({ form, onSubmit, isSaving, mode }: QnAKbFormProps) => {
  const { t } = useTranslation();
  const { isValid } = form.formState;

  const questionValue = form.watch('question') || '';
  const answerValue = form.watch('answer') || '';

  const isQuestionValid = questionValue.replace(/\s+/g, ' ').trim().length >= 5;
  const isAnswerValid = answerValue.replace(/\s+/g, ' ').trim().length >= 100;
  const isFormValid = isValid && isQuestionValid && isAnswerValid;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('QUESTION')}</FormLabel>
              <FormControl>
                <Textarea
                  rows={2}
                  placeholder={t('QUESTION_PLACEHOLDER')}
                  className="min-h-fit resize-none"
                  {...field}
                  readOnly={mode === 'view'}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="answer"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('ANSWER')}</FormLabel>
              <FormControl>
                <MarkdownEditor value={field.value} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex items-center justify-end gap-4">
          <DialogClose asChild>
            <Button variant="outline" className="w-20" size="sm">
              {t('CANCEL')}
            </Button>
          </DialogClose>
          {mode === 'add' && (
            <Button
              type="submit"
              size="sm"
              className="min-w-20"
              disabled={isSaving || !isFormValid}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('ADDING')}
                </>
              ) : (
                t('ADD')
              )}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};
